import { supabaseClient } from '@auth-v2';
import { useMutation } from '@tanstack/react-query';

import { ModuleParameterUpdateBody, parametersBatchUpdate, parametersUpdate } from '@data';
import { activeUserIdAtom, useEnsureAtomValue } from '@store';

import { queryClient } from '../config';
import { queryKeys } from '../queryKeys';

import { getCustomModuleCustomizationsKey, getCustomTableMutationKey } from './useCustomModule';

export const useParametersUpdate = (layoutId: number, moduleId: string, showChildOn?: 'kiosk' | 'floor' | null) => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: getCustomModuleCustomizationsKey(layoutId, moduleId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layoutCustomization.base(activeUserId, layoutId),
            });
        },
        mutationKey: getCustomTableMutationKey(layoutId, moduleId),
        mutationFn: async (selectedParameterIds: string[]) => {
            return await parametersUpdate(supabaseClient, {
                layoutId,
                moduleId,
                showChildOn,
                parameterIds: selectedParameterIds,
            });
        },
    });
};

// TODO: rework `useParametersUpdate` to be generic, not tied to customModule.
export const useUpdateParameters = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useMutation({
        onSuccess: (layout) => {
            if (layout)
                queryClient.invalidateQueries({
                    queryKey: queryKeys.layoutCustomization.base(activeUserId, layout.id),
                });
        },
        mutationFn: async (args: {
            moduleId: string;
            parameterIds: string[];
            showChildOn?: 'kiosk' | 'floor' | null;
            layoutId: number;
        }) => {
            return await parametersUpdate(supabaseClient, {
                ...args,
            });
        },
    });
};

export const useBatchUpdateParameters = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useMutation({
        onSuccess: (layout) => {
            if (layout)
                queryClient.invalidateQueries({
                    queryKey: queryKeys.layoutCustomization.base(activeUserId, layout.id),
                });
        },
        mutationFn: async (args: { updates: ModuleParameterUpdateBody[]; layoutId: number }) => {
            return await parametersBatchUpdate(supabaseClient, {
                ...args,
            });
        },
    });
};
